// src/pos-app/EnrollmentScreen.js
//
// F12a Task 4.2 — first-run device enrollment for the native POS app.
// Rendered by PosAppShell when no station config exists. Two steps:
//   1. Manager login  — binds this device to a tenant (AuthContext.login).
//   2. Station choice — cashier / waiter / KDS (+ kitchen/bar queue for KDS),
//      persisted via StationContext.saveStation. PosAppShell re-renders into
//      the chosen station automatically once the config lands — no navigation.
//
// Visual language: "Aurora Glass" (docs/mockups/f12a/*) — aurora gradient
// backdrop, frosted glass card, brand-blue primary. All colors come from the
// themeable CSS vars (C tokens), so light/dark flip automatically.
//
// Layout: a single centered glass column over the full-bleed aurora at every
// width. On phones (<768px) the compact card proportions apply; on tablets/
// desktops (>=768px) the same column simply scales up — wider card, larger
// brandmark/wordmark, bigger type, taller inputs and more generous station
// cards — so the page owns an iPad screen without any side panel. Pure CSS
// media query; logical properties keep RTL mirroring free.
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { categoryService } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { useStation } from '../station/StationContext';
import { STATIONS } from '../station/stationConfig';
import { FONT } from '../styles/theme';

/* ------------------------------------------------------------------ */
/* Stylesheet — uses the app's --c-* theme vars so both themes work.   */
/* ------------------------------------------------------------------ */
const CSS = `
.enr-root{position:fixed;inset:0;overflow-x:hidden;overflow-y:auto;background:var(--c-bg);font-family:${FONT};-webkit-font-smoothing:antialiased;}
.enr-aurora{position:fixed;inset:0;overflow:hidden;z-index:0;pointer-events:none;}
.enr-blob{position:absolute;border-radius:50%;filter:blur(90px);opacity:.45;will-change:transform;}
:root[data-theme="dark"] .enr-blob{opacity:.34;}
.enr-b1{width:46vw;height:46vw;left:-12vw;top:-14vw;background:radial-gradient(circle at 35% 35%,var(--c-green) 0%,transparent 70%);animation:enrDrift1 26s ease-in-out infinite alternate;}
.enr-b2{width:40vw;height:40vw;right:-10vw;top:-8vw;background:radial-gradient(circle at 60% 40%,var(--c-purple) 0%,transparent 70%);animation:enrDrift2 32s ease-in-out infinite alternate;}
.enr-b3{width:42vw;height:42vw;left:22vw;bottom:-18vw;background:radial-gradient(circle at 50% 50%,var(--c-teal) 0%,transparent 70%);animation:enrDrift3 29s ease-in-out infinite alternate;}
@keyframes enrDrift1{from{transform:translate(0,0) scale(1);}to{transform:translate(6vw,4vw) scale(1.12);}}
@keyframes enrDrift2{from{transform:translate(0,0) scale(1.05);}to{transform:translate(-5vw,5vw) scale(.95);}}
@keyframes enrDrift3{from{transform:translate(0,0) scale(1);}to{transform:translate(-4vw,-5vw) scale(1.1);}}
@keyframes enrIn{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);}}
@keyframes enrSpin{to{transform:rotate(360deg);}}
@media (prefers-reduced-motion: reduce){
  .enr-blob{animation:none !important;}
  .enr-card,.enr-queues{animation:none !important;}
}

.enr-shell{position:relative;z-index:1;display:flex;align-items:stretch;min-height:100%;}

.enr-wrap{flex:1;display:flex;align-items:center;justify-content:center;box-sizing:border-box;
  padding:calc(24px + var(--safe-top)) 20px calc(24px + var(--safe-bottom));}
.enr-card{width:100%;max-width:430px;box-sizing:border-box;border-radius:24px;padding:30px 28px 26px;
  background:var(--c-surface);
  background:color-mix(in srgb, var(--c-surface) 76%, transparent);
  -webkit-backdrop-filter:blur(22px) saturate(1.5);backdrop-filter:blur(22px) saturate(1.5);
  border:1px solid var(--c-border);
  border-color:color-mix(in srgb, var(--c-border) 55%, transparent);
  box-shadow:0 18px 50px rgba(2,8,23,.18);
  animation:enrIn .35s ease;}
.enr-card.enr-wide{max-width:680px;}

.enr-brand{display:flex;align-items:center;justify-content:center;gap:11px;margin-bottom:22px;}
/* No tile, no gradient — the mark sits on the card, matching how the brand is
   presented on the web portal and landing header. */
.enr-brandmark{width:42px;height:42px;flex:none;display:flex;align-items:center;justify-content:center;}
/* Dark ink on light surfaces, white variant on dark — a dark mark on a dark
   card would vanish. */
.enr-brandmark img{width:100%;height:100%;object-fit:contain;display:block;}
:root[data-theme="dark"] .enr-brandmark img.mark-dark{display:none;}
.enr-brandmark img.mark-light{display:none;}
:root[data-theme="dark"] .enr-brandmark img.mark-light{display:block;}
.enr-wordmark{font-size:19px;font-weight:800;letter-spacing:-.3px;color:var(--c-ink);}

.enr-eyebrow{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:1.4px;color:var(--c-green);text-align:center;margin:0 0 6px;}
.enr-title{font-size:22px;font-weight:800;letter-spacing:-.4px;color:var(--c-ink);text-align:center;margin:0 0 6px;}
.enr-sub{font-size:13.5px;font-weight:600;color:var(--c-muted);text-align:center;line-height:1.55;margin:0 auto 22px;max-width:420px;}

.enr-dots{display:flex;justify-content:center;gap:6px;margin-bottom:18px;}
.enr-dot{width:8px;height:8px;border-radius:99px;background:var(--c-border);transition:all .25s ease;}
.enr-dot.on{width:24px;background:var(--c-green);}

.enr-field{margin-bottom:14px;}
.enr-label{display:block;font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.8px;color:var(--c-muted);margin-bottom:7px;}
.enr-input{width:100%;min-height:50px;padding:0 16px;box-sizing:border-box;border-radius:14px;
  border:1.5px solid var(--c-border);background:var(--c-surface);color:var(--c-text);
  font-size:15px;font-weight:600;font-family:inherit;
  transition:border-color .15s ease, box-shadow .15s ease;}
.enr-input::placeholder{color:var(--c-mutedLight);font-weight:500;}
.enr-input:focus{outline:none;border-color:var(--c-green);box-shadow:0 0 0 3px var(--c-greenLight);}

.enr-error{display:flex;gap:10px;align-items:flex-start;padding:12px 14px;border-radius:13px;margin-bottom:14px;
  background:var(--c-redLight);border:1px solid var(--c-redBorder);color:var(--c-redText);
  font-size:13px;font-weight:600;line-height:1.5;animation:enrIn .2s ease;}
.enr-error svg{flex:none;margin-top:1px;}

.enr-btn{width:100%;min-height:54px;border:none;border-radius:15px;cursor:pointer;
  display:flex;align-items:center;justify-content:center;gap:10px;
  background:linear-gradient(180deg,var(--c-green) 0%,var(--c-greenDark) 100%);
  color:#fff;font-size:16px;font-weight:800;letter-spacing:-.2px;font-family:inherit;
  box-shadow:0 6px 18px color-mix(in srgb, var(--c-green) 35%, transparent), inset 0 1px 0 rgba(255,255,255,.18);
  transition:transform .15s ease, filter .15s ease, opacity .15s ease;}
.enr-btn:active{transform:scale(.97);filter:brightness(1.08);}
.enr-btn:disabled{opacity:.45;cursor:default;transform:none;filter:none;box-shadow:none;}
.enr-spin{animation:enrSpin .8s linear infinite;}

.enr-back{display:inline-flex;align-items:center;gap:7px;min-height:44px;padding:0 12px;margin:-6px 0 4px -12px;
  border:none;background:transparent;border-radius:11px;cursor:pointer;
  color:var(--c-muted);font-size:13px;font-weight:700;font-family:inherit;
  transition:color .15s ease, transform .12s ease;}
.enr-back:active{transform:scale(.95);color:var(--c-text);}
[dir="rtl"] .enr-back{margin:-6px -12px 4px 0;}
[dir="rtl"] .enr-back svg{transform:scaleX(-1);}

.enr-stations{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:16px;}
/* Phone (<=620px): 2x2, not a single column. There are FOUR stations now
   (Cashier / Table Service / Waiter Sales / KDS) and one column made the very
   first screen of the app a tall scroll on a 390px phone. Two columns fit all
   four in view; the cards trade a little padding and type size for the second
   column, and each tile is still ~140x120 — far above the 44px touch minimum. */
@media (max-width:620px){
  .enr-stations{grid-template-columns:repeat(2,1fr);gap:10px;margin-bottom:14px;}
  .enr-station{min-height:118px;padding:12px 11px;gap:7px;border-radius:14px;}
  .enr-station .enr-ic{width:34px;height:34px;border-radius:10px;}
  .enr-station .enr-ic svg{width:18px;height:18px;}
  .enr-station .enr-st-name{font-size:13.5px;line-height:1.25;}
  .enr-station .enr-st-desc{font-size:11px;line-height:1.4;}
  .enr-check{top:8px;inset-inline-end:8px;width:20px;height:20px;}
}
.enr-station{position:relative;display:flex;flex-direction:column;align-items:flex-start;gap:10px;
  min-height:128px;padding:16px 15px;box-sizing:border-box;border-radius:16px;cursor:pointer;
  border:1.5px solid var(--c-border);background:var(--c-surface);text-align:start;font-family:inherit;
  transition:transform .15s ease, border-color .15s ease, background .15s ease, box-shadow .15s ease;}
.enr-station:active{transform:scale(.97);}
.enr-station.sel{border-color:var(--c-green);background:var(--c-greenLight);box-shadow:0 0 0 3px var(--c-greenLight);}
.enr-station .enr-ic{width:44px;height:44px;border-radius:12px;flex:none;display:flex;align-items:center;justify-content:center;
  background:var(--c-greenLight);color:var(--c-green);transition:background .15s ease, color .15s ease;}
.enr-station.sel .enr-ic{background:var(--c-green);color:#fff;}
.enr-station .enr-st-name{font-size:15px;font-weight:800;color:var(--c-ink);letter-spacing:-.2px;}
.enr-station .enr-st-desc{font-size:12px;font-weight:600;color:var(--c-muted);line-height:1.45;}
.enr-check{position:absolute;top:10px;inset-inline-end:10px;width:22px;height:22px;border-radius:50%;
  display:flex;align-items:center;justify-content:center;background:var(--c-green);color:#fff;
  box-shadow:0 2px 6px color-mix(in srgb, var(--c-green) 40%, transparent);animation:enrIn .18s ease;}

.enr-queue-label{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.8px;color:var(--c-muted);margin:2px 0 8px;}
.enr-queues{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:16px;animation:enrIn .22s ease;}
.enr-queue{display:flex;align-items:center;justify-content:center;gap:9px;min-height:54px;border-radius:14px;cursor:pointer;
  border:1.5px solid var(--c-border);background:var(--c-surface);
  color:var(--c-slate);font-size:14px;font-weight:700;font-family:inherit;
  transition:transform .15s ease, border-color .15s ease, background .15s ease, color .15s ease;}
.enr-queue:active{transform:scale(.97);}
.enr-queue.sel{border-color:var(--c-green);background:var(--c-greenLight);color:var(--c-green);}

.enr-foot{margin-top:20px;text-align:center;font-size:11px;font-weight:800;letter-spacing:3px;text-transform:uppercase;color:var(--c-muted);opacity:.5;}

/* ------------------------------------------------------------------ */
/* Tablet / desktop (>=768px): the SAME single centered column, scaled */
/* up — wider card, larger brand + type, taller inputs, generous       */
/* station cards — so the page feels designed for an iPad, not a       */
/* floating phone card. Logical properties keep RTL free.              */
/* ------------------------------------------------------------------ */
@media (min-width:768px){
  .enr-wrap{padding:calc(40px + var(--safe-top)) 40px calc(40px + var(--safe-bottom));}
  .enr-card{max-width:660px;border-radius:30px;padding:48px 56px 40px;}
  .enr-card.enr-wide{max-width:720px;}
  .enr-brand{gap:14px;margin-bottom:30px;}
  .enr-brandmark{width:58px;height:58px;}
  .enr-wordmark{font-size:27px;}
  .enr-dots{margin-bottom:26px;}
  .enr-dot{width:9px;height:9px;}
  .enr-dot.on{width:28px;}
  .enr-eyebrow{font-size:12px;letter-spacing:1.8px;margin-bottom:8px;}
  .enr-title{font-size:31px;margin-bottom:8px;}
  .enr-sub{font-size:15.5px;max-width:520px;margin-bottom:30px;}
  .enr-field{margin-bottom:18px;}
  .enr-label{font-size:12px;margin-bottom:8px;}
  .enr-input{min-height:58px;font-size:17px;padding:0 18px;border-radius:16px;}
  .enr-btn{min-height:60px;font-size:17px;border-radius:17px;}
  .enr-back{font-size:14px;}
  .enr-error{font-size:14px;}
  .enr-stations{gap:14px;margin-bottom:20px;}
  .enr-station{min-height:164px;padding:22px 20px;border-radius:18px;gap:12px;}
  .enr-station .enr-ic{width:52px;height:52px;border-radius:14px;}
  .enr-station .enr-st-name{font-size:17px;}
  .enr-station .enr-st-desc{font-size:13px;}
  .enr-queue-label{font-size:12px;margin:4px 0 10px;}
  .enr-queues{gap:12px;margin-bottom:20px;}
  .enr-queue{min-height:60px;font-size:15px;}
  .enr-foot{margin-top:26px;}
}
`;

/* ------------------------------------------------------------------ */
/* Inline SVG icons (stroke = currentColor)                            */
/* ------------------------------------------------------------------ */
const Icon = {
    // Real brand asset, not a hand-rolled glyph. Served from public/ (CRA copies
    // public/ verbatim into build/, and scripts/prune-native-build.js only strips
    // downloads/, help-screenshots/ and *.map — root-level PNGs survive into the
    // Capacitor bundle).
    // Both variants render; CSS shows whichever suits the active theme. Only
    // the visible one carries alt text so the brand is announced once.
    brand: (
        <>
            <img className="mark-dark" src="/aqana-mark.png" alt="Aqana" />
            <img className="mark-light" src="/aqana-mark-light.png" alt="" aria-hidden="true" />
        </>
    ),
    cashier: (
        <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="2" y="7" width="20" height="12" rx="2" />
            <circle cx="12" cy="13" r="2.6" />
            <path d="M6 11h.01M18 15h.01" />
        </svg>
    ),
    waiter: (
        <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6M9 15h6M9 11h2" />
        </svg>
    ),
    kds: (
        <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="2" y="3" width="20" height="14" rx="2" />
            <path d="M8 21h8M12 17v4" />
        </svg>
    ),
    kitchen: (
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
        </svg>
    ),
    bar: (
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M8 22h8M12 11v11M19 3l-7 8-7-8h14z" />
        </svg>
    ),
    check: (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M20 6 9 17l-5-5" />
        </svg>
    ),
    alert: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v4M12 16h.01" />
        </svg>
    ),
    back: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
    ),
    arrow: (
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 12h14M13 6l6 6-6 6" />
        </svg>
    ),
};

function Spinner() {
    return (
        <svg className="enr-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-6.2-8.56" />
        </svg>
    );
}

function StepDots({ step }) {
    return (
        <div className="enr-dots" aria-hidden="true">
            <span className={`enr-dot${step === 1 ? ' on' : ''}`} />
            <span className={`enr-dot${step === 2 ? ' on' : ''}`} />
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Screen                                                              */
/* ------------------------------------------------------------------ */
export default function EnrollmentScreen() {
    // Aliased: the step-1 form field below is also named `tenantId` (the
    // 6-digit short ID the manager types in) — this is the tenant id
    // AuthContext resolves from that login, persisted into the station
    // config as the belt-and-braces fallback (see stationConfig.js).
    const { login, tenantId: authTenantId } = useAuth();
    const { saveStation } = useStation();
    const { t } = useLanguage();

    const [step, setStep] = useState(1);

    // step 1 — manager login (tenant short-ID + username + password,
    // mirroring LoginPage's tenant sign-in)
    const [tenantId, setTenantId] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [signingIn, setSigningIn] = useState(false);

    // step 2 — station choice
    const [station, setStation] = useState(null);
    const [kdsQueue, setKdsQueue] = useState(null);
    const [saveError, setSaveError] = useState('');
    const [saving, setSaving] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        if (signingIn) return;
        if (!tenantId.trim() || !username.trim() || !password) {
            setLoginError(t('all_fields_required'));
            return;
        }
        setLoginError('');
        setSigningIn(true);
        try {
            await login(username.trim(), password, tenantId.trim());
            setStep(2);
        } catch (err) {
            setLoginError(err?.response?.data?.detail || t('invalid_credentials'));
        } finally {
            setSigningIn(false);
        }
    };

    const pickStation = (id) => {
        setStation(id);
        if (id !== STATIONS.KDS) setKdsQueue(null);
        setSaveError('');
    };

    const canFinish = station != null && (station !== STATIONS.KDS || kdsQueue != null);

    const handleFinish = async () => {
        if (!canFinish || saving) return;
        setSaveError('');
        setSaving(true);
        try {
            // After this resolves, PosAppShell re-renders into the station.
            await saveStation(
                station === STATIONS.KDS
                    ? { station, kdsQueue, tenantId: authTenantId }
                    : { station, tenantId: authTenantId }
            );
        } catch (err) {
            setSaveError(t('enroll_save_failed'));
            setSaving(false);
        }
    };

    // Four stations, matching the web hub. Table Service and Waiter Sales are
    // different jobs on different hardware: Table Service is the FOH floor
    // plan (tablet), Waiter Sales is order taking at the table (phone or
    // tablet). Before F15 part 2 there was one tile labelled "Waiter" whose
    // description promised "Take orders at the table" while mounting the floor
    // plan — the description was right about the job and wrong about the
    // surface, and no surface did that job at all.
    const stationOptions = [
        { id: STATIONS.CASHIER, name: t('cashier'), desc: t('enroll_cashier_desc'), icon: Icon.cashier },
        { id: STATIONS.TABLE_SERVICE, name: t('table_service'), desc: t('enroll_table_service_desc'), icon: Icon.waiter },
        { id: STATIONS.WAITER_SALES, name: t('waiter_sales'), desc: t('enroll_waiter_sales_desc'), icon: Icon.waiter },
        { id: STATIONS.KDS, name: t('kds'), desc: t('enroll_kds_desc'), icon: Icon.kds },
    ];

    // One queue per kitchen-display category, the same list StationSettingsSheet
    // builds (StationSettingsSheet.js:519) and the web Functions page uses for
    // its KDS tiles.
    //
    // This used to be a hardcoded kitchen/bar pair. StationSettingsSheet was
    // moved to the live category list and enrollment was not, so a tenant with
    // Desserts, Grill or Snacks could not enrol a device onto any of them —
    // you had to finish setup on the wrong queue and change it in Settings
    // afterwards. Reported on a real device 2026-09-24.
    //
    // Empty on failure, which falls through to the legacy pair below: a failed
    // fetch must not leave a new device with nothing to select.
    const [kdsCategories, setKdsCategories] = useState([]);

    useEffect(() => {
        let cancelled = false;
        categoryService.getAll()
            .then((cats) => {
                if (cancelled) return;
                const names = (cats || [])
                    .filter((c) => c && c.is_kitchen_display)
                    .map((c) => c.name)
                    .filter(Boolean)
                    .sort((a, b) => a.localeCompare(b));
                setKdsCategories([...new Set(names)]);
            })
            .catch(() => { if (!cancelled) setKdsCategories([]); });
        return () => { cancelled = true; };
        // Keyed on the KDS selection, NOT mounted once. StationSettingsSheet
        // learned this the hard way (see its identical effect): a single
        // failed or empty fetch at boot degrades the picker to the legacy
        // kitchen/bar pair permanently, with no way back but an app restart.
        //
        // Enrollment is the worse place for it — this screen runs moments
        // after login, so a fetch fired on mount can lose the race with the
        // token and there is no second chance before the device is committed
        // to a queue. Selecting KDS is both the moment the list is needed and
        // a point where auth is certainly settled.
    }, [station]);

    const queueOptions = kdsCategories.length
        ? kdsCategories.map((name) => ({ id: name, name }))
        : [
            { id: 'kitchen', name: t('kitchen'), icon: Icon.kitchen },
            { id: 'bar', name: t('bar'), icon: Icon.bar },
        ];

    return (
        <div className="enr-root">
            <style>{CSS}</style>

            <div className="enr-aurora" aria-hidden="true">
                <div className="enr-blob enr-b1" />
                <div className="enr-blob enr-b2" />
                <div className="enr-blob enr-b3" />
            </div>

            <div className="enr-shell">
                <div className="enr-wrap">
                    <div className={`enr-card${step === 2 ? ' enr-wide' : ''}`}>
                        <div className="enr-brand">
                            <div className="enr-brandmark">{Icon.brand}</div>
                            <span className="enr-wordmark">Aqana</span>
                        </div>

                        <StepDots step={step} />

                        {step === 1 ? (
                            <form onSubmit={handleLogin} noValidate>
                                {/* First-run copy (F15 part 2). This is the very
                                    first screen anyone sees — including an App
                                    Store reviewer — and it used to read
                                    "Device setup / Manager sign in / Sign in
                                    with your manager account…", which is a
                                    back-office login, not a product. It now
                                    welcomes and says what the app is. */}
                                <p className="enr-eyebrow">{t('enroll_welcome_eyebrow')}</p>
                                <h1 className="enr-title">{t('enroll_welcome_title')}</h1>
                                <p className="enr-sub">{t('enroll_welcome_sub')}</p>

                                {loginError && (
                                    <div className="enr-error" role="alert">
                                        {Icon.alert}
                                        <span>{loginError}</span>
                                    </div>
                                )}

                                <div className="enr-field">
                                    <label className="enr-label" htmlFor="enr-tenant">{t('tenant_id')}</label>
                                    <input
                                        id="enr-tenant"
                                        className="enr-input"
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={6}
                                        placeholder={t('six_digit_id')}
                                        value={tenantId}
                                        onChange={(e) => setTenantId(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        autoComplete="off"
                                        autoCapitalize="none"
                                        autoCorrect="off"
                                        spellCheck="false"
                                        disabled={signingIn}
                                        required
                                    />
                                </div>
                                <div className="enr-field">
                                    <label className="enr-label" htmlFor="enr-username">{t('username')}</label>
                                    <input
                                        id="enr-username"
                                        className="enr-input"
                                        type="text"
                                        placeholder={t('enter_username')}
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        autoComplete="username"
                                        autoCapitalize="none"
                                        autoCorrect="off"
                                        spellCheck="false"
                                        disabled={signingIn}
                                        required
                                    />
                                </div>
                                <div className="enr-field" style={{ marginBottom: 20 }}>
                                    <label className="enr-label" htmlFor="enr-password">{t('password')}</label>
                                    <input
                                        id="enr-password"
                                        className="enr-input"
                                        type="password"
                                        placeholder={t('enter_password')}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        autoComplete="current-password"
                                        disabled={signingIn}
                                        required
                                    />
                                </div>

                                <button className="enr-btn" type="submit" disabled={signingIn}>
                                    {signingIn ? <Spinner /> : null}
                                    {signingIn ? t('signing_in') : t('sign_in')}
                                    {!signingIn && Icon.arrow}
                                </button>
                            </form>
                        ) : (
                            <div>
                                <button className="enr-back" type="button" onClick={() => setStep(1)}>
                                    {Icon.back}
                                    {t('back')}
                                </button>

                                <p className="enr-eyebrow">{t('enroll_device_setup')}</p>
                                <h1 className="enr-title">{t('enroll_station_title')}</h1>
                                <p className="enr-sub">{t('enroll_station_sub')}</p>

                                <div className="enr-stations">
                                    {stationOptions.map((opt) => (
                                        <button
                                            key={opt.id}
                                            type="button"
                                            className={`enr-station${station === opt.id ? ' sel' : ''}`}
                                            onClick={() => pickStation(opt.id)}
                                            aria-pressed={station === opt.id}
                                        >
                                            {station === opt.id && <span className="enr-check">{Icon.check}</span>}
                                            <span className="enr-ic">{opt.icon}</span>
                                            <span className="enr-st-name">{opt.name}</span>
                                            <span className="enr-st-desc">{opt.desc}</span>
                                        </button>
                                    ))}
                                </div>

                                {station === STATIONS.KDS && (
                                    <div>
                                        <p className="enr-queue-label">{t('enroll_kds_queue_label')}</p>
                                        <div className="enr-queues">
                                            {queueOptions.map((q) => (
                                                <button
                                                    key={q.id}
                                                    type="button"
                                                    className={`enr-queue${kdsQueue === q.id ? ' sel' : ''}`}
                                                    onClick={() => { setKdsQueue(q.id); setSaveError(''); }}
                                                    aria-pressed={kdsQueue === q.id}
                                                >
                                                    {q.icon}
                                                    {q.name}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {saveError && (
                                    <div className="enr-error" role="alert">
                                        {Icon.alert}
                                        <span>{saveError}</span>
                                    </div>
                                )}

                                <button
                                    className="enr-btn"
                                    type="button"
                                    onClick={handleFinish}
                                    disabled={!canFinish || saving}
                                >
                                    {saving ? <Spinner /> : null}
                                    {saving ? t('enroll_finishing') : t('enroll_finish')}
                                    {!saving && Icon.arrow}
                                </button>
                            </div>
                        )}

                        <div className="enr-foot">Aqana</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
